/**
 * OrgScopeAssignmentModal (S2.4) — per assigned role, anchor the grant at an
 * organisation unit and choose how far it reaches down the tree (Axis-B).
 *
 * The org anchor picker REUSES the shared category `TaxonomyTreeEditor`
 * directly (plan §Slice 2.4 — "tree-first, do NOT fork a second picker");
 * `ORG_ANCHOR_TREE` is exported so the S2.4 reuse test can assert component
 * IDENTITY, not just similarity. RECORDED DIVERGENCE (ux-review): S2.1's
 * `OrgTreeEditor` could NOT wrap this shared editor (it is hard-typed to
 * `ProductCategory[]` with no title-decorator seam — see its docblock) and
 * hand-rolls an AntD Tree, so the one org hierarchy currently renders in two
 * tree grammars (tab editor vs this picker); noted in `docs/rbac.md`, with
 * the follow-up below converging both onto one component. The editor is
 * consumed in pick-only posture through its public contract:
 *   • the adapter leaf-caps every node at `MAX_CATEGORY_DEPTH`, so the editor's
 *     own `canAddChild` guard renders NO inline add-child affordance (no dead
 *     `+` button — quick-create belongs to the S2.1 org tree, not a picker);
 *   • a capture-phase `onDragStart` preventDefault on the wrapper suppresses
 *     the editor's drag-to-reparent locally (`onMove` therefore never fires).
 *   Follow-up recorded to grow TaxonomyTreeEditor an explicit `selectOnly`
 *   prop once its owner frees the file.
 *
 * Modes: `本組織` (self) / `本組織及所有下層` (self_and_subtree);
 * `subtree_unassigned` is designed into the resolver but OWNER-GATED OFF
 * (Open Q5) — the option is only offered when the gate flips.
 *
 * The read-only `ReadinessMeter` shows the RESULTING Axis-B reach ("sees N
 * 組織單位") resolved live through the very same pure `resolveEffectiveAccess`
 * the MSW stamp uses (shared-selector discipline, plan §Resolver contract) —
 * franchise walls and inactive exclusions are therefore identical to what the
 * scoped list handlers will enforce after the write.
 *
 * The write itself is reason-gated: the whole surface rides inside
 * `ReasonRequiredModal`, and the submitted reason lands in the audit lens via
 * the governance PATCH (`field: 'assignments'`).
 */

import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Select, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import TaxonomyTreeEditor from '@/components/common/TaxonomyTreeEditor';
import ReadinessMeter from '@/components/common/ReadinessMeter';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { useFormStyles } from '@/styles/formStyles';
import { radiusTokens } from '@/styles/tokens';
import { resolveEffectiveAccess } from '@/lib/rbac';
import type { OrgScopeMode, RoleAssignment } from '@/lib/rbac';
import { MAX_CATEGORY_DEPTH } from '@/types/api/productCategory';
import type { CategoryLevelConfig, ProductCategory } from '@/types/api/productCategory';
import type { GovernanceRole, GovernanceUser, OrganisationUnit, OrgModel } from './model';

const { Text } = Typography;

/* eslint-disable react-refresh/only-export-components -- the reuse-assertion
   binding and the two pure adapters are exported for the S2.4 node-env test
   (same precedent as the ProtectedRoute guard-predicate export); they are
   constants / pure functions, so a fast-refresh boundary miss here is inert. */

/**
 * THE asserted reuse criterion (plan §Slice 2.4 acceptance): the anchor picker
 * IS the shared category `TaxonomyTreeEditor` — NOT a picker fork. (S2.1's
 * `OrgTreeEditor` does not wrap it — recorded divergence, see file docblock.)
 * The S2.4 test asserts identity against this export.
 */
export const ORG_ANCHOR_TREE = TaxonomyTreeEditor;

/** Open Q5 (Workday `subtree_unassigned` dedup) — owner-gated OFF in v1. */
const SUBTREE_UNASSIGNED_ENABLED: boolean = false;

/** One role's draft anchor; `null` = deliberately un-anchored (no Axis-B reach). */
export interface DraftAnchor {
  orgId: string;
  mode: OrgScopeMode;
}

/** Per-role draft the modal edits — keyed by the user's held `roleIds`. */
export type DraftMap = Record<string, DraftAnchor | null>;

/**
 * Adapt org units to the `TaxonomyTreeEditor` node shape (pure, exported for
 * the S2.4 test). Every node is LEAF-CAPPED at `MAX_CATEGORY_DEPTH` so the
 * editor's `canAddChild` guard is false everywhere — the pick-only posture
 * falls out of the component's own public contract, no fork. `path` is the
 * id-chain the editor's search expansion expects (adjacency stays the source
 * of truth — same doctrine as the Axis-B resolver). The secondary line carries
 * the localized 直營/加盟 operating-model label, so a franchise compartment is
 * always visibly marked in TEXT (never colour-only) right in the picker.
 */
export function orgUnitsToPickerNodes(
  orgs: readonly OrganisationUnit[],
  modelLabel: (model: OrgModel) => string,
  primaryZh: boolean
): ProductCategory[] {
  const byId = new Map(orgs.map((org) => [org.id, org]));
  const idPath = (org: OrganisationUnit): string => {
    const chain: string[] = [];
    let cur: OrganisationUnit | undefined = org;
    const seen = new Set<string>();
    while (cur && !seen.has(cur.id)) {
      chain.unshift(cur.id);
      seen.add(cur.id);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    return chain.join('/');
  };
  return orgs.map((org, index) => ({
    id: org.id,
    code: org.code,
    parentId: org.parentId ?? null,
    // Leaf-cap: depth === MAX ⇒ childLevel > MAX ⇒ no add-child affordance.
    depth: MAX_CATEGORY_DEPTH,
    path: idPath(org),
    sortOrder: index,
    // TaxonomyTreeEditor renders primary = (isZh ? nameZh : nameEn) and the
    // OTHER field as the secondary line — map the org name to the primary slot
    // and the operating-model label (直營/加盟) to the secondary slot.
    nameEn: primaryZh ? modelLabel(org.model) : org.name,
    nameZh: primaryZh ? org.name : modelLabel(org.model),
    isActive: org.status === 'active',
  }));
}

/** Draft → canonical `assignments[]` write shape (pure, exported for the test). */
export function draftToAssignments(draft: DraftMap, roleIds: readonly string[]): RoleAssignment[] {
  const out: RoleAssignment[] = [];
  for (const roleId of roleIds) {
    const anchor = draft[roleId];
    if (anchor) out.push({ roleId, orgId: anchor.orgId, mode: anchor.mode });
  }
  return out;
}

/* eslint-enable react-refresh/only-export-components */

/**
 * v1 edits ONE anchor per role (the UI is also the only assignments writer, so
 * a multi-anchor record can only predate this surface — its first anchor seeds
 * the draft and the save normalizes to one-per-role).
 */
function initialDraft(user: GovernanceUser): DraftMap {
  const draft: DraftMap = {};
  for (const roleId of user.roleIds) {
    const existing = (user.assignments ?? []).find((a) => a.roleId === roleId);
    draft[roleId] = existing ? { orgId: existing.orgId, mode: existing.mode } : null;
  }
  return draft;
}

/** Never-reached callbacks — see the pick-only posture notes in the header. */
const noopAddChild = (): void => undefined;
const noopMove = (): void => undefined;

export interface OrgScopeAssignmentModalProps {
  open: boolean;
  user: GovernanceUser | null;
  roles: GovernanceRole[];
  organisations: OrganisationUnit[];
  /** Receives the normalized `assignments[]` plus the mandatory audit reason. */
  onSubmit: (
    assignments: RoleAssignment[],
    reason: string
  ) => void | boolean | Promise<void | boolean>;
  onCancel: () => void;
}

const OrgScopeAssignmentModal = ({
  open,
  user,
  roles,
  organisations,
  onSubmit,
  onCancel,
}: OrgScopeAssignmentModalProps) => {
  const { t, i18n } = useTranslation('governance');
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const isZh = i18n.language.startsWith('zh');

  const [draft, setDraft] = useState<DraftMap>({});

  useEffect(() => {
    if (open && user) setDraft(initialDraft(user));
  }, [open, user]);

  const orgById = useMemo(
    () => new Map(organisations.map((org) => [org.id, org])),
    [organisations]
  );

  const pickerNodes = useMemo(
    () => orgUnitsToPickerNodes(organisations, (model) => t(`model.${model}`), isZh),
    [organisations, t, isZh]
  );

  // Labels are never rendered by the editor (they only drive its enabled-level
  // set); localized anyway so no literal copy lives in src.
  const pickerLevels = useMemo<CategoryLevelConfig[]>(() => {
    const label = t('fields.organisation');
    return [{ level: MAX_CATEGORY_DEPTH, labelEn: label, labelZh: label, enabled: true }];
  }, [t]);

  const draftAssignments = useMemo(
    () => draftToAssignments(draft, user?.roleIds ?? []),
    [draft, user]
  );

  /**
   * Data-honesty notice (ux-review): a pre-existing multi-anchor record (seed/
   * import — this modal only writes one-per-role) would be silently narrowed
   * on save. Name the roles so the operator KNOWS the extra anchors drop.
   */
  const multiAnchorRoleNames = useMemo(() => {
    if (!user) return [];
    return user.roleIds
      .filter(
        (roleId) => (user.assignments ?? []).filter((a) => a.roleId === roleId).length > 1
      )
      .map((roleId) => roles.find((role) => role.id === roleId)?.name ?? roleId);
  }, [user, roles]);

  // The meter's count — resolved through the SAME pure resolver the MSW stamp
  // consumes, over the live catalogs and the DRAFT assignments, so the shown
  // reach (franchise walls, inactive exclusions included) is exactly what the
  // scoped list handlers will enforce after the write.
  const reachCount = useMemo(() => {
    if (!user) return 0;
    return resolveEffectiveAccess({
      identity: {
        userId: user.id,
        roleIds: [...user.roleIds],
        homeOrgId: user.homeOrgId,
        assignments: draftAssignments,
      },
      roles,
      orgs: organisations,
    }).visibleOrgIds.size;
  }, [user, draftAssignments, roles, organisations]);

  const modeOptions = useMemo(() => {
    const modes: OrgScopeMode[] = SUBTREE_UNASSIGNED_ENABLED
      ? ['self', 'self_and_subtree', 'subtree_unassigned']
      : ['self', 'self_and_subtree'];
    return modes.map((mode) => ({ value: mode, label: t(`assignment.mode.${mode}`) }));
  }, [t]);

  const meterItems = useMemo(
    () =>
      (user?.roleIds ?? []).map((roleId) => {
        const roleName = roles.find((role) => role.id === roleId)?.name ?? roleId;
        const anchor = draft[roleId];
        const anchorOrg = anchor ? orgById.get(anchor.orgId) : undefined;
        return {
          key: roleId,
          label: roleName,
          detail:
            anchor && anchorOrg
              ? `${anchorOrg.name} · ${t(`assignment.mode.${anchor.mode}`)}`
              : t('assignment.noAnchor'),
          complete: Boolean(anchor && anchorOrg),
        };
      }),
    [user, roles, draft, orgById, t]
  );

  /** Does this anchor's compartment involve a 加盟 franchise wall? */
  const touchesFranchise = (anchorId: string): boolean => {
    const anchor = orgById.get(anchorId);
    if (!anchor) return false;
    if (anchor.model === 'franchise') return true;
    const isUnderAnchor = (org: OrganisationUnit): boolean => {
      let cur: OrganisationUnit | undefined = org;
      const seen = new Set<string>();
      while (cur && !seen.has(cur.id)) {
        if (cur.id === anchorId) return true;
        seen.add(cur.id);
        cur = cur.parentId ? orgById.get(cur.parentId) : undefined;
      }
      return false;
    };
    return organisations.some(
      (org) => org.model === 'franchise' && org.id !== anchorId && isUnderAnchor(org)
    );
  };

  return (
    <ReasonRequiredModal
      open={open && user !== null}
      title={t('assignment.modalTitle')}
      submitText={t('reasonModal.submit')}
      cancelText={t('reasonModal.cancel')}
      reasonLabel={t('reasonModal.reasonLabel')}
      requiredMessage={t('reasonModal.reasonRequired')}
      reasonPlaceholder={t('reasonModal.reasonPlaceholder')}
      description={user ? `${user.name} · ${user.email}` : undefined}
      width={640}
      onSubmit={(reason) => onSubmit(draftAssignments, reason)}
      onCancel={onCancel}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
        {t('assignment.hint')}
      </Text>

      {/* Narrowing notice — surfaced BEFORE the reason gate, never silent. */}
      {multiAnchorRoleNames.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: token.marginMD }}
          message={t('assignment.multiAnchorNotice', {
            roles: multiAnchorRoleNames.join(' · '),
          })}
        />
      )}

      {(user?.roleIds ?? []).map((roleId) => {
        const roleName = roles.find((role) => role.id === roleId)?.name ?? roleId;
        const anchor = draft[roleId] ?? null;
        const anchorOrg = anchor ? orgById.get(anchor.orgId) : undefined;
        return (
          <div key={roleId} style={{ marginBottom: token.marginLG }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: token.marginXXS,
              }}
            >
              <Text strong>{roleName}</Text>
              {anchor && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setDraft((prev) => ({ ...prev, [roleId]: null }))}
                >
                  {t('assignment.clearAnchor')}
                </Button>
              )}
            </div>

            <Text style={formStyles.labelStyle}>{t('assignment.anchorLabel')}</Text>
            <div
              // Pick-only posture: suppress the shared editor's drag-to-reparent
              // locally (capture phase, before the Tree's own dragstart runs).
              onDragStartCapture={(event) => event.preventDefault()}
              style={{
                maxHeight: 240,
                overflow: 'auto',
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: radiusTokens.card,
                padding: token.paddingXS,
                marginBottom: token.marginSM,
              }}
            >
              <TaxonomyTreeEditor
                nodes={pickerNodes}
                levels={pickerLevels}
                selectedId={anchor?.orgId ?? null}
                onSelect={(orgId) =>
                  setDraft((prev) => ({
                    ...prev,
                    // Least-privilege default for a fresh anchor: `self`.
                    [roleId]: { orgId, mode: prev[roleId]?.mode ?? 'self' },
                  }))
                }
                onAddChild={noopAddChild}
                onMove={noopMove}
              />
            </div>

            {anchor && anchorOrg && (
              <div>
                <Text style={formStyles.labelStyle}>{t('assignment.modeLabel')}</Text>
                <Select
                  size="large"
                  style={{ width: '100%' }}
                  value={anchor.mode}
                  options={modeOptions}
                  onChange={(mode: OrgScopeMode) =>
                    setDraft((prev) => ({ ...prev, [roleId]: { orgId: anchor.orgId, mode } }))
                  }
                />
              </div>
            )}

            {anchor && touchesFranchise(anchor.orgId) && (
              <Text
                type="secondary"
                style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
              >
                {t('assignment.franchiseHint')}
              </Text>
            )}
          </div>
        );
      })}

      {/* Read-only reach meter — no item is clickable by construction. */}
      <div style={{ marginBottom: token.marginMD }}>
        <ReadinessMeter
          title={t('assignment.reachTitle')}
          ready={meterItems.length > 0 && meterItems.every((item) => item.complete)}
          statusText={t('assignment.reach', { count: reachCount })}
          items={meterItems}
        />
      </div>
    </ReasonRequiredModal>
  );
};

export default OrgScopeAssignmentModal;
